import { useState, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useSearch } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'motion/react'
import { FaMosque } from 'react-icons/fa6'
import {
  LuUser,
  LuMail,
  LuBookOpen,
  LuMessageSquare,
  LuArrowRight,
  LuArrowLeft,
  LuSparkles,
  LuShield,
  LuClock,
  LuHeart,
  LuCalendar,
  LuUsers,
} from 'react-icons/lu'
import { CheckCircle } from 'lucide-react'

import { FormInput } from '#/components/common/fields/FormInput'
import { FormPhoneInput } from '#/components/common/fields/FormPhoneInput'
import { FormSelect } from '#/components/common/fields/FormSelect'
import { FormCombobox } from '#/components/common/fields/FormCombobox'
import PageTransition from '#/components/common/PageTransition'
import { PlanPicker } from '#/components/common/fields/PlanPicker'
import { Button } from '#/components/ui/button'
import { cn } from '#/lib/utils'
import { useSubscribeMutation } from '#/features/subscribe/hooks/useSubscribeMutation'
import { useCourses } from '#/features/courses/hooks/useCourses'
import { COUNTRY_OPTIONS } from '#/constants/countries'

// ─── Static data ───────────────────────────────────────────────────────────────

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male (ذكر)' },
  { value: 'female', label: 'Female (أنثى)' },
]

const TRUST_ITEMS = [
  {
    icon: LuShield,
    label: 'Secure & Private',
    sub: 'Your data is never shared',
  },
  { icon: LuClock, label: '24h Response', sub: "We'll get back to you fast" },
  { icon: LuHeart, label: 'No Commitment', sub: 'Free trial class included' },
]

// ─── Step config ───────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Your Details', icon: LuUser },
  { id: 2, label: 'Course & Plan', icon: LuBookOpen },
]

// ─── Form type ─────────────────────────────────────────────────────────────────
type SubscribeFormData = {
  name: string
  gender: string
  age: string
  email: string
  phone: string
  country: string
  message: string
  courseId: string
  plan: string
}

// ─── Islamic tile pattern ──────────────────────────────────────────────────────
function IslamicPattern({ opacity = '0.04' }: { opacity?: string }) {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="sub-tile"
          x="0"
          y="0"
          width="90"
          height="90"
          patternUnits="userSpaceOnUse"
        >
          <g fill="none" stroke="#ffb606" strokeWidth="0.6">
            <polygon points="45,4 49,33 72,20 57,40 85,45 57,50 72,70 49,57 45,86 41,57 18,70 33,50 5,45 33,40 18,20 41,33" />
            <polygon
              points="45,20 48,34 60,28 54,42 68,45 54,48 60,62 48,56 45,70 42,56 30,62 36,48 22,45 36,42 30,28 42,34"
              strokeWidth="0.3"
            />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#sub-tile)" />
    </svg>
  )
}

// ─── Step progress indicator ───────────────────────────────────────────────────
function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 px-8 py-5">
      {STEPS.map((step, i) => {
        const done = current > step.id
        const active = current === step.id
        const Icon = step.icon

        return (
          <div key={step.id} className="flex items-center">
            {/* Circle */}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-300',
                  done
                    ? 'border-secondary-500 bg-secondary-500 text-white'
                    : active
                      ? 'border-primary-500 bg-primary-500 text-neutral-900 shadow-md shadow-primary-200'
                      : 'border-neutral-200 bg-white text-neutral-400',
                )}
              >
                {done ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              <span
                className={cn(
                  'text-[11px] font-semibold tracking-wide whitespace-nowrap',
                  active
                    ? 'text-primary-600'
                    : done
                      ? 'text-secondary-600'
                      : 'text-neutral-400',
                )}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line (not after last) */}
            {i < STEPS.length - 1 && (
              <div
                className="mx-3 mb-5 h-px w-16 transition-all duration-500"
                style={{
                  background: done
                    ? 'var(--secondary-500)'
                    : 'var(--neutral-200)',
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Success screen ────────────────────────────────────────────────────────────
function SuccessScreen({
  name,
  onReset,
}: {
  name: string
  onReset: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center gap-6 py-10 text-center"
    >
      <div className="relative flex h-24 w-24 items-center justify-center">
        <div className="absolute inset-0 animate-ping rounded-full bg-secondary-500/15" />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-secondary-200 bg-secondary-50">
          <CheckCircle className="h-10 w-10 text-secondary-500" />
        </div>
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-neutral-900">
          Jazāk Allāhu Khayran, {name}!
        </h2>
        <p className="mx-auto max-w-sm text-base leading-relaxed text-neutral-500">
          Your enrollment request has been received. Our team will reach out
          within 24 hours to confirm your course and schedule your first class.
        </p>
      </div>
      <div className="mt-2 flex flex-col gap-3 ">
        <Link to="/courses" className="w-full">
          <Button variant="outline" className="gap-2 w-full">
            <LuBookOpen className="h-4 w-4" /> Explore More Courses
          </Button>
        </Link>
        <Button onClick={onReset} className="gap-2">
          Submit Another Request <LuArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

// ─── Slide animation variants ──────────────────────────────────────────────────
const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -40 : 40 }),
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function SubscribePage() {
  const search = useSearch({ strict: false }) as {
    course?: string
    plan?: string
    duration?: string
  }

  const initialPlan = (() => {
    if (
      search.plan === '30min' ||
      search.plan === '45min' ||
      search.plan === '60min'
    )
      return search.plan
    if (
      search.duration === '30min' ||
      search.duration === '45min' ||
      search.duration === '60min'
    )
      return search.duration
    if (search.plan?.includes('45min')) return '45min'
    if (search.plan?.includes('60min') || search.plan?.includes('1hour'))
      return '60min'
    return '45min'
  })()

  const [step, setStep] = useState(1)
  const [direction, setDirection] = useState(1) // 1 = forward, -1 = back
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<{
    type: 'info' | 'warning' | 'error'
    text: string
  } | null>(null)

  const methods = useForm<SubscribeFormData>({
    defaultValues: {
      name: '',
      gender: '',
      age: '',
      email: '',
      phone: '',
      country: '',
      message: '',
      courseId: search.course ?? '',
      plan: initialPlan,
    },
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting },
    watch,
    reset,
  } = methods
  const studentName = watch('name')

  const subscribeMutation = useSubscribeMutation()
  const { data: coursesRes } = useCourses({ limit: 100 })
  const coursesList = (coursesRes?.data || []).map((c) => ({
    value: c.id,
    label: c.title,
    sublabel: `${c.level} · ${c.language}`,
    slug: c.slug,
  }))
  const courseOptions = coursesList.length > 0 ? coursesList : []

  // Resolve matching courseId from search param (slug or id) ONCE on initial load
  const courseInitializedRef = useRef(false)
  useEffect(() => {
    if (!search.course || courseInitializedRef.current) return
    const matched = courseOptions.find(
      (c) =>
        c.value === search.course ||
        (c as any).slug === search.course ||
        c.label.toLowerCase() === search.course?.toLowerCase(),
    )
    if (matched) {
      methods.setValue('courseId', matched.value, { shouldValidate: true })
      courseInitializedRef.current = true
    }
  }, [search.course, courseOptions, methods])

  // Resolve plan from search param ONCE on initial load
  const planInitializedRef = useRef(false)
  useEffect(() => {
    if (!search.plan || planInitializedRef.current) return
    const resolvedPlan = (() => {
      if (
        search.plan === '30min' ||
        search.plan === '45min' ||
        search.plan === '60min'
      )
        return search.plan
      if (search.plan.includes('45min')) return '45min'
      if (search.plan.includes('60min') || search.plan.includes('1hour'))
        return '60min'
      return '30min'
    })()
    if (resolvedPlan) {
      methods.setValue('plan', resolvedPlan, { shouldValidate: true })
      planInitializedRef.current = true
    }
  }, [search.plan, methods])

  // Validate step-1 fields before advancing
  const handleNext = async () => {
    const ok = await trigger([
      'name',
      'gender',
      'age',
      'email',
      'phone',
      'country',
    ])
    if (!ok) return
    setDirection(1)
    setStep(2)
  }

  const handleBack = () => {
    setDirection(-1)
    setStep(1)
  }

  const onSubmit = async (data: SubscribeFormData) => {
    setErrorMessage(null)
    try {
      await subscribeMutation.mutateAsync({
        courseId: data.courseId || courseOptions[0]?.value,
        planKey: data.plan || '30min',
        name: data.name,
        gender: (data.gender as any) || 'male',
        age: Number(data.age) || 20,
        email: data.email,
        phone: data.phone,
        country: data.country,
        message: data.message,
      })
      setSubmitted(true)
    } catch (err: any) {
      if (err?.status === 409) {
        setErrorMessage({
          type: 'info',
          text: "You're already enrolled in this course! Please check your email for access instructions.",
        })
      } else if (err?.status === 400) {
        setErrorMessage({
          type: 'warning',
          text:
            err.message || 'Please wait a short while before re-subscribing.',
        })
      } else if (err?.status === 429) {
        setErrorMessage({
          type: 'warning',
          text: 'Too many requests. Please wait a moment and try again.',
        })
      } else {
        setErrorMessage({
          type: 'error',
          text:
            err?.message ||
            'Unable to submit enrollment. Please check your information and try again.',
        })
      }
    }
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-neutral-50">
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden noir-section pb-14 pt-32">
          <IslamicPattern />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(42% 60% at 15% 30%, rgba(255,182,6,0.10) 0%, transparent 70%)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(35% 50% at 85% 75%, rgba(0,77,64,0.15) 0%, transparent 70%)',
            }}
          />

          <div className="container relative z-10 mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex justify-center"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-900/40 px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-300 backdrop-blur-sm">
                <LuSparkles className="h-3 w-3 animate-pulse" /> Begin Your
                Journey <LuSparkles className="h-3 w-3 animate-pulse" />
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="text-4xl font-bold leading-tight text-white sm:text-5xl xl:text-6xl"
            >
              Enroll in a{' '}
              <span className="relative text-primary-400">
                Sacred Course
                <span
                  aria-hidden
                  className="absolute left-0 right-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.18 }}
              className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-neutral-300"
            >
              Learn the Quran, Arabic and Islamic sciences with qualified
              scholars — from anywhere in the world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.26 }}
              className="mt-5 flex justify-center gap-2 text-sm text-neutral-400"
            >
              <Link
                to="/"
                className="flex items-center gap-1.5 transition-colors hover:text-primary-300"
              >
                <FaMosque className="text-primary-500" /> Home
              </Link>
              <span className="text-neutral-600">/</span>
              <span className="text-primary-400">Enroll Now</span>
            </motion.div>
          </div>
        </section>

        {/* ── Main ─────────────────────────────────────────────────── */}
        <section className="container mx-auto py-14">
          <div className="grid gap-10 lg:grid-cols-[1fr_500px] xl:gap-14 ">
            {/* ── Left info panel ─────────────────────────────── */}
            <div className="flex flex-col gap-8 max-lg:order-2">
              {/* Why Wa'allamah card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="relative overflow-hidden rounded-2xl bg-secondary-900 px-8 py-10 text-white"
              >
                <IslamicPattern />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(60% 70% at 10% 20%, rgba(0,179,149,0.12) 0%, transparent 70%)',
                  }}
                />
                <div className="relative z-10">
                  <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-900/50 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-primary-300">
                    <LuSparkles className="h-3 w-3" /> Why Wa'allamah?
                  </span>
                  <h2 className="mb-3 text-3xl font-bold leading-tight">
                    Authentic Knowledge,
                    <br />
                    <span className="text-primary-400">Sacred Tradition</span>
                  </h2>
                  <p className="mb-8 text-sm leading-relaxed text-neutral-300">
                    Our scholars hold certified Ijāzahs tracing back to the
                    Prophet ﷺ. Every class is structured, measurable, and
                    spiritually enriching.
                  </p>
                  <ul className="space-y-3.5">
                    {[
                      'One-on-one sessions with a certified teacher',
                      'Flexible scheduling — your timezone, your pace',
                      'Progress tracking & structured curriculum',
                      'Free first class — no commitment required',
                      'Certificates upon course completion',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                        </span>
                        <span className="text-sm leading-relaxed text-neutral-200">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-3">
                {TRUST_ITEMS.map((item, i) => {
                  const Icon = item.icon
                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + i * 0.06 }}
                      className="flex flex-col items-center gap-2.5 rounded-xl border border-neutral-200 bg-white p-4 text-center"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-50">
                        <Icon className="h-4 w-4 text-secondary-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral-800">
                          {item.label}
                        </p>
                        <p className="mt-0.5 text-[11px] text-neutral-500">
                          {item.sub}
                        </p>
                      </div>
                    </motion.div>
                  )
                })}
              </div>

              {/* Testimonial */}
              <motion.blockquote
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="relative rounded-2xl border border-primary-200 bg-primary-50 px-8 py-6"
              >
                <div className="absolute -top-3.5 left-8 flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-lg font-bold text-white shadow">
                  "
                </div>
                <p className="text-sm italic leading-relaxed text-neutral-700">
                  I enrolled two years ago knowing only how to read the
                  alphabet. Today I've memorized 5 juz and can read with proper
                  tajweed. The teachers are patient and genuinely caring.
                </p>
                <footer className="mt-4 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-bold text-secondary-700">
                    SA
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">
                      Sister Amina
                    </p>
                    <p className="text-xs text-neutral-500">
                      Hifz student — London, UK
                    </p>
                  </div>
                </footer>
              </motion.blockquote>
            </div>

            {/* ── Right: multi-step form ─────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="self-start"
            >
              <div className=" rounded-3xl  border border-neutral-200 bg-white shadow-xl shadow-neutral-200/60">
                {/* Card header */}
                <div className="relative rounded-t-3xl overflow-hidden bg-secondary-900 px-8 pb-5 pt-7">
                  <IslamicPattern />
                  <div className="relative z-10 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/20">
                      <LuBookOpen className="h-5 w-5 text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        Enrollment Form
                      </h2>
                      <p className="text-xs text-neutral-400">
                        Step {submitted ? '✓' : step} of {STEPS.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step indicator */}
                {!submitted && <StepIndicator current={step} />}

                {/* Divider */}
                <div className="mx-8 h-px bg-neutral-100" />

                {/* Form body */}
                <div className="px-8 py-7">
                  <AnimatePresence mode="wait" custom={direction}>
                    {/* ─ SUCCESS ─ */}
                    {submitted && (
                      <SuccessScreen
                        key="success"
                        name={studentName || 'Student'}
                        onReset={() => {
                          reset()
                          setStep(1)
                          setSubmitted(false)
                        }}
                      />
                    )}

                    {/* ─ STEP 1: Personal Details ─ */}
                    {!submitted && step === 1 && (
                      <motion.div
                        key="step1"
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                      >
                        <FormProvider {...methods}>
                          <div className="space-y-4">
                            <p className="text-sm text-neutral-500">
                              Tell us a little about yourself so we can match
                              you with the right teacher.
                            </p>

                            {/* Name */}
                            <FormInput
                              name="name"
                              label="Full Name"
                              placeholder="e.g. Ali Mohammed"
                              icon={<LuUser />}
                              rules={{
                                required: 'Your name is required',
                                minLength: {
                                  value: 2,
                                  message: 'Name is too short',
                                },
                              }}
                            />

                            {/* Gender + Age */}
                            <div className="grid grid-cols-2 gap-3">
                              <FormSelect
                                name="gender"
                                label="Gender"
                                placeholder="Select"
                                icon={<LuUsers />}
                                options={GENDER_OPTIONS}
                                rules={{ required: 'Required' }}
                              />
                              <FormInput
                                name="age"
                                label="Age"
                                type="number"
                                placeholder="e.g. 25"
                                icon={<LuCalendar />}
                                rules={{
                                  required: 'Required',
                                  min: { value: 4, message: 'Min 4' },
                                  max: { value: 100, message: 'Max 100' },
                                }}
                              />
                            </div>

                            {/* Email */}
                            <FormInput
                              name="email"
                              label="Email Address"
                              type="email"
                              placeholder="e.g. ali@example.com"
                              icon={<LuMail />}
                              rules={{
                                required: 'Email is required',
                                pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                  message: 'Enter a valid email',
                                },
                              }}
                            />

                            {/* Phone & Country */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <FormPhoneInput
                                name="phone"
                                label="Phone / WhatsApp"
                                placeholder="Enter phone number"
                                defaultCountry="EG"
                                rules={{
                                  required:
                                    'Phone / WhatsApp number is required',
                                }}
                              />

                              <FormCombobox
                                name="country"
                                label="Country"
                                placeholder="Select your country"
                                searchPlaceholder="Search country..."
                                options={COUNTRY_OPTIONS}
                                rules={{ required: 'Country is required' }}
                              />
                            </div>
                            {/* Message */}
                            <div className="flex flex-col gap-1.5">
                              <label
                                htmlFor="message"
                                className="text-sm font-medium text-neutral-700"
                              >
                                Notes{' '}
                                <span className="text-neutral-400 font-normal">
                                  (optional)
                                </span>
                              </label>
                              <div className="relative overflow-hidden rounded-xl border border-neutral-200 bg-white transition-all duration-200 hover:border-neutral-300 focus-within:border-primary-500 focus-within:ring-3 focus-within:ring-primary-500/15">
                                <span className="pointer-events-none absolute left-4 top-3.5 text-neutral-400">
                                  <LuMessageSquare />
                                </span>
                                <textarea
                                  id="message"
                                  {...methods.register('message')}
                                  rows={3}
                                  placeholder="Your current level, goals, or any questions..."
                                  className="w-full resize-none bg-transparent pb-4 pl-11 pr-4 pt-3 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none"
                                />
                              </div>
                            </div>

                            {/* Next button */}
                            <Button
                              type="button"
                              onClick={handleNext}
                              className="mt-1 h-12 w-full gap-2 text-base font-semibold"
                            >
                              Continue
                              <LuArrowRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </FormProvider>
                      </motion.div>
                    )}

                    {/* ─ STEP 2: Course & Plan ─ */}
                    {!submitted && step === 2 && (
                      <motion.div
                        key="step2"
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                      >
                        <FormProvider {...methods}>
                          <form
                            onSubmit={handleSubmit(onSubmit)}
                            noValidate
                            className="space-y-5"
                          >
                            <p className="text-sm text-neutral-500">
                              Choose the course you want to study and a plan
                              that fits your schedule.
                            </p>

                            {/* Course combobox */}
                            <div className="relative">
                              <FormCombobox
                                name="courseId"
                                label="Select Course"
                                placeholder="Choose a course..."
                                searchPlaceholder="Type to search..."
                                icon={<LuBookOpen />}
                                options={courseOptions}
                                rules={{ required: 'Please select a course' }}
                              />
                            </div>

                            {/* Plan picker */}
                            <div className="rounded-2xl border border-neutral-100 bg-neutral-50/70 p-4">
                              <PlanPicker
                                name="plan"
                                rules={{ required: 'Please choose a plan' }}
                              />
                            </div>

                            {/* Error / info feedback */}
                            {errorMessage && (
                              <div
                                role="alert"
                                className={cn(
                                  'rounded-xl border p-3.5 text-xs font-medium leading-relaxed',
                                  errorMessage.type === 'info' &&
                                    'border-secondary-300 bg-secondary-50 text-secondary-800',
                                  errorMessage.type === 'warning' &&
                                    'border-amber-300 bg-amber-50 text-amber-900',
                                  errorMessage.type === 'error' &&
                                    'border-red-300 bg-red-50 text-red-900',
                                )}
                              >
                                {errorMessage.text}
                              </div>
                            )}

                            {/* Back + Submit */}
                            <div className="flex gap-3">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleBack}
                                className="h-12 gap-1.5"
                              >
                                <LuArrowLeft className="h-4 w-4" /> Back
                              </Button>
                              <Button
                                type="submit"
                                className="h-12 flex-1 gap-2 text-base font-semibold"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <>
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    Enroll Now
                                    <LuArrowRight className="h-4 w-4" />
                                  </>
                                )}
                              </Button>
                            </div>
                          </form>
                        </FormProvider>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  )
}
